import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { setUserContext, clearUserContext } from '@/lib/sentry';

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn';
export type AccountType = 'school_student' | 'consumer';

interface AuthContextType {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
  accountType: AccountType | null;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resendVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [accountType, setAccountType] = useState<AccountType | null>(null);

  // Helper function to fetch account type from profiles table
  const fetchAccountType = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('account_type')
        .eq('user_id', userId)
        .single();
      
      if (!error && data) {
        setAccountType(data.account_type as AccountType);
      } else {
        // Fallback to school_student if fetch fails (safe default for existing users)
        setAccountType('school_student');
      }
    } catch (err) {
      console.error('Error fetching account type:', err);
      // Safe default for existing users
      setAccountType('school_student');
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setStatus(session ? 'signedIn' : 'signedOut');
      
      // Fetch account type if user exists
      if (session?.user) {
        fetchAccountType(session.user.id);
      }
    }).catch((error) => {
      console.error('Failed to get initial session:', error);
      setStatus('signedOut');
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setStatus(session ? 'signedIn' : 'signedOut');
      
      // Update Sentry user context
      if (session?.user) {
        setUserContext({
          id: session.user.id,
          email: session.user.email,
        });
        // Fetch account type for signed in user
        fetchAccountType(session.user.id);
      } else {
        clearUserContext();
        // Clear account type on sign out
        setAccountType(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
    
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const resendVerification = async () => {
    if (!user?.email) return;
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
    });
    
    if (error) {
      console.error('Error resending verification:', error);
      throw error;
    }
  };

  const value = {
    status,
    user,
    session,
    accountType,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}